import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ChessBoard from './ChessBoard';
import ChessNav from './ChessNav';
import Icon from '../Shared/Icons';
import { getDailyPuzzles, getPuzzlesByPhase, PHASES } from '../../utils/chessPuzzles';
import { C, FONT } from '../../utils/chessTheme';

// ── Puzzles — Ufunguzi · Katikati · Mwisho ─────────────────────────────
//  • Puzzles za Leo (tatu kwa siku, streak).
//  • Au chagua awamu ya mchezo unayotaka kufundishwa.
// Zote zimehakikiwa kwa sheria za chess.

const DAY_KEY = 'iyanna_puzzle_day';
const STREAK_KEY = 'iyanna_puzzle_streak';
const SOLVED_KEY = 'iyanna_puzzle_solved';

function todayStr() { return new Date().toISOString().slice(0, 10); }
function loadDay() {
  try {
    const d = JSON.parse(localStorage.getItem(DAY_KEY));
    if (d && d.date === todayStr() && Array.isArray(d.solved)) return d;
  } catch {}
  return { date: todayStr(), solved: [false, false, false] };
}
function saveDay(d) { try { localStorage.setItem(DAY_KEY, JSON.stringify(d)); } catch {} }
function loadStreak() {
  try { return JSON.parse(localStorage.getItem(STREAK_KEY)) || { count: 0, lastDate: null }; }
  catch { return { count: 0, lastDate: null }; }
}
function loadSolvedIds() {
  try { return JSON.parse(localStorage.getItem(SOLVED_KEY)) || []; } catch { return []; }
}
function saveSolvedIds(ids) { try { localStorage.setItem(SOLVED_KEY, JSON.stringify(ids)); } catch {} }

function sideToMove(fen) { return (fen.split(' ')[1] === 'b') ? 'b' : 'w'; }

const LEVEL_STYLE = {
  Rahisi: { bg: 'rgba(95,208,138,0.16)', color: '#5FD08A' },
  Wastani: { bg: 'rgba(52,211,153,0.16)', color: '#34D399' },
  Ngumu: { bg: 'rgba(248,113,113,0.16)', color: '#FCA5A5' },
};

export default function ChessPuzzle() {
  const navigate = useNavigate();
  const [view, setView] = useState('home'); // 'home' | 'daily' | phase id
  const [dailyPuzzles] = useState(getDailyPuzzles());
  const [day, setDay] = useState(loadDay());
  const [streak, setStreak] = useState(() => loadStreak().count);
  const [solvedIds, setSolvedIds] = useState(loadSolvedIds());
  const [current, setCurrent] = useState(0);
  const [fen, setFen] = useState('');
  const [status, setStatus] = useState('playing');
  const [showHint, setShowHint] = useState(false);
  const [attempts, setAttempts] = useState(0);

  const isDaily = view === 'daily';
  const phaseMeta = PHASES.find(p => p.id === view);
  const puzzles = isDaily ? dailyPuzzles : phaseMeta ? getPuzzlesByPhase(view) : [];
  const puzzle = puzzles[current];

  function markDailySolved(index) {
    const next = { ...day, solved: day.solved.map((v, i) => (i === index ? true : v)) };
    setDay(next); saveDay(next);
    if (next.solved.every(Boolean)) {
      const today = todayStr(); const prev = loadStreak();
      if (prev.lastDate !== today) {
        const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
        const n = { count: prev.lastDate === yesterday ? prev.count + 1 : 1, lastDate: today };
        try { localStorage.setItem(STREAK_KEY, JSON.stringify(n)); } catch {}
        setStreak(n.count);
      }
    }
  }
  function markThemeSolved(p) {
    if (solvedIds.includes(p.id)) return;
    const next = [...solvedIds, p.id]; setSolvedIds(next); saveSolvedIds(next);
  }
  function open(target) {
    const list = target === 'daily' ? dailyPuzzles : getPuzzlesByPhase(target);
    if (!list.length) return;
    const idx = target === 'daily'
      ? Math.max(0, day.solved.findIndex(x => !x))
      : 0;
    setView(target); setCurrent(idx); setFen(list[idx].fen);
    const solved = target === 'daily' ? day.solved[idx] : solvedIds.includes(list[idx].id);
    setStatus(solved ? 'solved' : 'playing'); setShowHint(false); setAttempts(0);
  }
  function goTo(index) {
    setCurrent(index); setFen(puzzles[index].fen);
    const solved = isDaily ? day.solved[index] : solvedIds.includes(puzzles[index].id);
    setStatus(solved ? 'solved' : 'playing'); setShowHint(false); setAttempts(0);
  }
  function handleMove(mv) {
    const correct = mv.from === puzzle.solution.from && mv.to === puzzle.solution.to;
    if (correct) {
      setFen(mv.fen); setStatus('solved');
      if (isDaily) markDailySolved(current); else markThemeSolved(puzzle);
    } else {
      setAttempts(a => a + 1); setStatus('failed');
      setTimeout(() => { setFen(puzzle.fen); setStatus('playing'); }, 850);
    }
  }

  // ── HOME ─────────────────────────────────────────────────────────────
  if (view === 'home') {
    const solvedToday = day.solved.filter(Boolean).length;
    return (
      <div style={styles.page}>
        <button style={styles.back} onClick={() => navigate('/chess')}>
          <Icon name="door" size={20} color={C.text} strokeWidth={2.4} />
        </button>
        <div style={styles.head}>
          <div style={styles.title}>Puzzles</div>
        </div>

        <button style={styles.dailyCard} onClick={() => open('daily')}>
          <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
            <div style={styles.dailyTitle}>Za Leo</div>
            {streak > 0 && <div style={styles.streakTag}><Icon name="bolt" size={12} color="#FBBF24" /> Siku {streak}</div>}
          </div>
          <div style={styles.dailyProgress}>{solvedToday}/3</div>
          <span style={styles.chev}>›</span>
        </button>

        <div style={styles.phaseGrid}>
          {PHASES.map(ph => {
            const list = getPuzzlesByPhase(ph.id);
            const done = list.filter(p => solvedIds.includes(p.id)).length;
            const complete = done === list.length && list.length > 0;
            return (
              <button key={ph.id} style={styles.phaseCard} onClick={() => open(ph.id)}>
                <div style={{ ...styles.phaseIcon, ...(complete ? styles.phaseIconDone : {}) }}>
                  <Icon name={ph.icon} size={20} color={complete ? '#06281A' : C.accent} strokeWidth={2} />
                </div>
                <div style={styles.phaseName}>{ph.name}</div>
                <div style={{ ...styles.phaseCount, ...(complete ? { color: C.accent } : {}) }}>
                  {done}/{list.length}
                </div>
              </button>
            );
          })}
        </div>

        <ChessNav />
      </div>
    );
  }

  // ── PLAYER ───────────────────────────────────────────────────────────
  const lvl = LEVEL_STYLE[puzzle.level] || LEVEL_STYLE.Wastani;
  const solvedFlags = isDaily ? day.solved : puzzles.map(p => solvedIds.includes(p.id));
  const nextUnsolved = solvedFlags.findIndex((s, i) => !s && i !== current);
  const allDone = solvedFlags.every(Boolean);
  const toMove = sideToMove(puzzle.fen);

  return (
    <div style={styles.page}>
      <style>{`@keyframes iyFade { from { opacity: 0 } to { opacity: 1 } }`}</style>
      <button style={styles.back} onClick={() => setView('home')}>
        <Icon name="door" size={20} color={C.text} strokeWidth={2.4} />
      </button>
      <div style={styles.head}>
        <div style={styles.title}>{isDaily ? 'Za Leo' : phaseMeta.name}</div>
        <div style={{ ...styles.levelBadge, background: lvl.bg, color: lvl.color }}>{puzzle.level}</div>
        <div style={styles.toMove}>{toMove === 'w' ? 'Weupe wanacheza' : 'Weusi wanacheza'} · pata pigo bora</div>

        <div style={styles.dotsRow}>
          {puzzles.map((_, i) => (
            <button key={i}
              style={{ ...styles.dot, ...(solvedFlags[i] ? styles.dotSolved : {}), ...(i === current ? styles.dotActive : {}) }}
              onClick={() => goTo(i)}>
              {solvedFlags[i] ? '✓' : i + 1}
            </button>
          ))}
        </div>
      </div>

      <ChessBoard
        fen={fen}
        onMove={handleMove}
        interactive={status !== 'solved'}
        playerColor={toMove}
        orientation={toMove === 'b' ? 'black' : 'white'}
      />

      {/* WIN POPUP — pops in front of the screen on solve */}
      {status === 'solved' && (
        <div style={styles.winOverlay} onClick={() => {
          if (!allDone && nextUnsolved !== -1) goTo(nextUnsolved);
        }}>
          <div style={styles.winCard} onClick={e => e.stopPropagation()}>
            <div style={styles.winBurst}><Icon name="check" size={34} color="#06281A" strokeWidth={3} /></div>
            <div style={styles.winTitle}>Umeshinda!</div>
            <div style={styles.winSkill}>{puzzle.lessonSkill}</div>
            {allDone ? (
              <div style={styles.winDone}>
                {isDaily ? 'Umemaliza zote za leo!' : 'Umemaliza awamu hii yote!'}
              </div>
            ) : nextUnsolved !== -1 ? (
              <button style={styles.winNext} onClick={() => goTo(nextUnsolved)}>Inayofuata →</button>
            ) : null}
            <button style={styles.winClose} onClick={() => setView('home')}>Funga</button>
          </div>
        </div>
      )}

      <div style={styles.statusBox}>
        {status === 'failed' && <div style={styles.failedMsg}>Sio sahihi — jaribu tena</div>}
        {status === 'playing' && (
          <>
            <button style={styles.hintBtn} onClick={() => setShowHint(!showHint)}>
              {showHint ? 'Ficha Hint' : 'Hint'}
            </button>
            {showHint && <div style={styles.hintText}>{puzzle.hint}</div>}
          </>
        )}
      </div>

      <ChessNav />
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: C.bgGradient, padding: '18px 16px 100px', position: 'relative' },
  back: { position: 'absolute', top: '16px', left: '14px', width: '42px', height: '42px', borderRadius: '13px', background: 'rgba(255,255,255,0.07)', border: `1px solid ${C.line}`, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 5 },
  head: { maxWidth: '560px', margin: '0 auto 14px', textAlign: 'center', paddingTop: '6px' },
  title: { color: C.text, fontFamily: FONT.display, fontSize: '24px', fontWeight: '700' },

  dailyCard: { maxWidth: '560px', margin: '0 auto 20px', width: '100%', display: 'flex', alignItems: 'center', gap: '14px', background: 'linear-gradient(140deg, rgba(52,211,153,0.16), rgba(14,159,110,0.05))', border: '1.5px solid rgba(52,211,153,0.45)', borderRadius: '18px', padding: '16px 18px', cursor: 'pointer', fontFamily: 'inherit' },
  dailyTitle: { color: C.text, fontFamily: FONT.display, fontSize: '18px', fontWeight: '800' },
  streakTag: { display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#FBBF24', fontSize: '11.5px', fontWeight: '800', marginTop: '4px' },
  dailyProgress: { color: C.accent, fontFamily: FONT.display, fontSize: '22px', fontWeight: '800' },
  chev: { color: C.textFaint, fontSize: '22px' },

  phaseGrid: { maxWidth: '560px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' },
  phaseCard: { background: 'linear-gradient(160deg, #262D2A, #1E2421)', border: `1px solid ${C.line}`, borderRadius: '16px', padding: '18px 10px', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '9px' },
  phaseIcon: { width: '44px', height: '44px', borderRadius: '13px', background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  phaseIconDone: { background: 'linear-gradient(135deg, #6EE7A8, #34D399)', border: 'none' },
  phaseName: { color: C.text, fontSize: '13.5px', fontWeight: '800' },
  phaseCount: { color: C.textDim, fontSize: '11px', fontWeight: '800' },

  levelBadge: { display: 'inline-block', marginTop: '8px', fontSize: '11px', fontWeight: '800', padding: '5px 14px', borderRadius: '20px' },
  toMove: { color: C.textDim, fontSize: '12px', marginTop: '7px' },
  dotsRow: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px', marginTop: '12px', flexWrap: 'wrap' },
  dot: { width: '36px', height: '36px', borderRadius: '12px', border: '1.5px solid rgba(255,255,255,0.22)', background: 'rgba(255,255,255,0.06)', color: 'rgba(236,239,234,0.7)', fontSize: '12.5px', fontWeight: '800', cursor: 'pointer', fontFamily: 'inherit' },
  dotSolved: { background: 'linear-gradient(135deg, rgba(95,208,138,0.32), rgba(46,158,107,0.2))', borderColor: '#5FD08A', color: '#DFF5E7' },
  dotActive: { borderColor: '#34D399', boxShadow: '0 0 0 3px rgba(52,211,153,0.28)' },

  statusBox: { maxWidth: '560px', margin: '16px auto', textAlign: 'center' },
  winOverlay: { position: 'fixed', inset: 0, zIndex: 900, background: 'rgba(8,11,10,0.7)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', animation: 'iyFade 0.2s ease' },
  winCard: { width: '100%', maxWidth: '300px', textAlign: 'center', background: 'linear-gradient(180deg, #2A302D, #1C221F)', border: '1px solid rgba(52,211,153,0.4)', borderRadius: '24px', padding: '28px 24px', boxShadow: '0 26px 70px rgba(0,0,0,0.6)' },
  winBurst: { width: '64px', height: '64px', borderRadius: '50%', margin: '0 auto 14px', background: 'linear-gradient(135deg, #6EE7A8, #34D399)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 10px 30px rgba(52,211,153,0.5)' },
  winTitle: { color: C.text, fontFamily: FONT.display, fontSize: '24px', fontWeight: '800' },
  winSkill: { color: C.textDim, fontSize: '12.5px', lineHeight: 1.5, marginTop: '8px', fontStyle: 'italic' },
  winDone: { color: '#4ADE80', fontSize: '13px', fontWeight: '800', marginTop: '14px' },
  winNext: { marginTop: '18px', width: '100%', background: 'linear-gradient(135deg, #6EE7A8, #22C55E 55%, #0E9F6E)', color: '#06281A', border: 'none', padding: '13px', borderRadius: '12px', fontSize: '14px', fontWeight: '800', cursor: 'pointer', fontFamily: 'inherit' },
  winClose: { marginTop: '10px', background: 'transparent', border: 'none', color: C.textDim, fontSize: '12.5px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' },
  failedMsg: { background: 'rgba(239,83,80,0.13)', border: '1px solid rgba(239,83,80,0.38)', color: '#EF9A9A', padding: '12px', borderRadius: '13px', fontSize: '13px', fontWeight: '700' },
  hintBtn: { background: 'rgba(255,255,255,0.08)', border: '1.5px solid rgba(52,211,153,0.35)', color: '#34D399', padding: '12px 28px', borderRadius: '13px', fontSize: '13.5px', fontWeight: '800', cursor: 'pointer', fontFamily: 'inherit' },
  hintText: { marginTop: '10px', color: C.textDim, fontSize: '12.5px', fontStyle: 'italic' },
};
